import { get } from 'lodash';

import RouterHistory from '@reverbdotcom/commons/dist/cms/lib/router_history';
import { V3 } from '../../api_request';
import { RECENT_SEARCH, SALE_SUGGESTION_TYPE } from './site_search';
import { Paths } from '../../shared/url_helpers';
import Suggestion from './suggestion';

import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/dist/elog/mparticle_tracker';
import { shouldClientSideResolve } from '@reverbdotcom/commons/dist/components/core_link';

import {
  reverb_search_CompletionType as CompletionType,
} from '../../../schemas/rql';

const SELECT_INTERACTION = 'select';
const SUBMIT_INTERACTION = 'submit';

interface ISearchInteractionEvent {
  position?: number;
  entityId?: number;
  interactionType?: string;
  context: {
    fullTextQuery?: string;
    type?: string;
    scope?: string;
    suggestions?: string[];
    query?: string;
  };
}

export default class ReverbSiteSearchController {
  static checkForRedirect(query: string) {
    const res = V3.get(Paths.apiListingRedirects.expand({}), { query });
    res.fail((_xhr, status, err) => {
      throw new Error(`failed to check for redirect - status: ${status}, err: ${err}`);
    });
    return res;
  }

  static navigateWindow(href: string): void {
    window.location.href = href;
  }

  static async redirectTo(url: string, trackingParams: ISearchInteractionEvent) {
    const clientSide = await shouldClientSideResolve(url);

    trackEvent({
      eventName: MParticleEventName.SiteSearch,
      position: trackingParams.position,
      suggestionId: String(trackingParams.entityId || ''),
      suggestionType: trackingParams.context.type,
      suggestionQuery: trackingParams.context.query,
      suggestionCount: get(trackingParams, 'context.suggestions.length', 0),
      interactionType: trackingParams.interactionType,
      targetUrl: url,
    });

    if (clientSide) {
      RouterHistory.push(url);
    } else {
      ReverbSiteSearchController.navigateWindow(url);
    }
  }

  static selectSuggestion(suggestion: Suggestion, suggestions: Suggestion[], lastTypedValue = ''): void {
    if (suggestion.type === RECENT_SEARCH) {
      ReverbSiteSearchController.submitRecentQuery(suggestion, suggestions, lastTypedValue);
      return;
    }

    const trackingEvent: ISearchInteractionEvent = {
      interactionType: SELECT_INTERACTION,
      position: suggestion.position,
      entityId: get(suggestion, 'completion.id'),
      context: {
        fullTextQuery: suggestion.value,
        type: suggestion.type,
        query: lastTypedValue,
        scope: suggestion.context,
        suggestions: ReverbSiteSearchController.suggestionValues(suggestions),
      },
    };

    if (CompletionType[suggestion.type] || suggestion.type === SALE_SUGGESTION_TYPE) {
      ReverbSiteSearchController.redirectTo(suggestion.url, trackingEvent);
      return;
    }

    const uri = new URL(suggestion.url);
    const hostlessUrl = suggestion.url.replace(uri.origin, '');

    if (suggestion.context) {
      ReverbSiteSearchController.redirectTo(hostlessUrl, trackingEvent);
    } else {
      ReverbSiteSearchController.checkForRedirect(suggestion.value)
        .then(resp =>
          ReverbSiteSearchController.redirectTo(resp.redirect || hostlessUrl, trackingEvent),
        );
    }
  }

  private static submitRecentQuery(suggestion: Suggestion, suggestions: Suggestion[], lastTypedValue = ''): void {
    const query = suggestion.value;
    const path = Paths.marketplace.expand({ query });

    const trackingEvent = {
      interactionType: SELECT_INTERACTION,
      position: suggestion.position,
      context: {
        fullTextQuery: query,
        type: RECENT_SEARCH,
        query: lastTypedValue,
        suggestions: ReverbSiteSearchController.suggestionValues(suggestions),
      },
    };

    ReverbSiteSearchController.checkForRedirect(query).then((resp) => {
      ReverbSiteSearchController.redirectTo(resp.redirect || path, trackingEvent);
    });
  }

  static submitQuery(query: string, suggestions: Suggestion[], lastTypedValue = ''): void {
    const path = Paths.marketplace.expand({ query });

    const trackingEvent = {
      interactionType: SUBMIT_INTERACTION,
      context: {
        fullTextQuery: query,
        query: lastTypedValue,
        suggestions: ReverbSiteSearchController.suggestionValues(suggestions),
      },
    };

    ReverbSiteSearchController.checkForRedirect(query).then((resp) => {
      ReverbSiteSearchController.redirectTo(resp.redirect || path, trackingEvent);
    });
  }

  private static suggestionValues(suggestions: Suggestion[]): string[] {
    return suggestions ? suggestions.map(s => s.value) : [];
  }
}



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/discovery-ui/src/components/discovery/site_search/reverb_site_search_controller.ts